import React, { useEffect, useState, useRef } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from "recharts";
import { dashboardApi } from "../../api/dashboardApi";

type Param = { label: string; value: number; unit: string; ok: boolean; rawValue?: string };

type ConnectionData = {
  status: "ok" | "degraded" | "down";
  params: Param[];
  lastUpdate: string;
};

type HistoryPoint = {
  time: string;
  timestamp: number;
  [key: string]: string | number;
};

function getStatusLabel(status: string) {
  switch (status) {
    case "ok":
      return "Connesso";
    case "degraded":
      return "Degradato";
    case "down":
      return "Disconnesso";
    default:
      return status;
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case "ok":
      return "success";
    case "degraded":
      return "warning";
    case "down":
      return "danger";
    default:
      return "secondary";
  }
}

const MAX_HISTORY = 48; // ~24 min a 30s di refresh
const REFRESH_MS = 30_000;

function toDataKey(label: string): string {
  return label.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
}

export default function MonitorMultiparametrico() {
  const [data, setData] = useState<ConnectionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const historyRef = useRef<HistoryPoint[]>([]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const res = await dashboardApi.getMultiparametric();
      setData(res);

      // Aggiorna storico per grafico (latenza ms + potenza segnale 0-100)
      const latencyParams = res.params.filter(
        (p) => p.unit === "ms" && typeof p.value === "number" && p.value >= 0
      );
      const potenzaParam = res.params.find((p) => p.label === "Potenza segnale");
      if (latencyParams.length > 0 || potenzaParam) {
        const ts = new Date();
        const point: HistoryPoint = {
          time: ts.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
          timestamp: ts.getTime(),
        };
        latencyParams.forEach((p) => {
          point[toDataKey(p.label)] = p.value;
        });
        if (potenzaParam && typeof potenzaParam.value === "number") {
          point.Potenza_segnale = potenzaParam.value;
        }
        historyRef.current = [...historyRef.current.slice(-(MAX_HISTORY - 1)), point];
        setHistory(historyRef.current);
      }
    } catch (e: any) {
      setErr(e?.message ?? "Errore caricamento");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const fmtTime = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  if (loading && !data) {
    return (
      <div className="card">
        <div className="card-header fw-semibold">Il Monitor Multiparametrico</div>
        <div className="card-body text-muted">Caricamento…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="card border-danger">
        <div className="card-header fw-semibold">Il Monitor Multiparametrico</div>
        <div className="card-body text-danger">{err}</div>
      </div>
    );
  }

  const status = data?.status ?? "down";
  const params = data?.params ?? [];

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div className="fw-semibold">Il Monitor Multiparametrico</div>
        <div className="d-flex align-items-center gap-2">
          <span className={`badge text-bg-${getStatusColor(status)}`}>
            {getStatusLabel(status)}
          </span>
          <span className="badge bg-secondary small">
            Aggiornato: {data?.lastUpdate ? fmtTime(data.lastUpdate) : "—"}
          </span>
          <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
            {loading ? "…" : "↻"}
          </button>
        </div>
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">
          Qualità della connessione Internet: latenza e disponibilità verso server esterni.
          I dati corrispondono alla <strong>linea TIM (red12c)</strong> alla quale è connesso il server.
        </p>

        <div className="row g-3">
          {params.map((p) => {
            const borderClass =
              p.rawValue === "Forte"
                ? "border-success"
                : p.rawValue === "Media"
                  ? "border-warning"
                  : p.rawValue === "Bassa"
                    ? "border-danger"
                    : p.ok
                      ? "border-success"
                      : "border-danger";
            return (
            <div
              key={p.label}
              className={`col-6 col-md-4 col-lg-2 border rounded px-3 py-2 ${borderClass}`}
            >
              <div className="small text-muted">{p.label}</div>
              <div className="fw-bold">
                {p.rawValue != null ? (
                  p.rawValue
                ) : p.value >= 0 ? (
                  <>
                    {typeof p.value === "number" && !Number.isInteger(p.value)
                      ? p.value.toFixed(1)
                      : p.value}
                    {p.unit ? ` ${p.unit}` : ""}
                  </>
                ) : (
                  "—"
                )}
              </div>
              <div className="small">
                {p.rawValue === "Forte" ? (
                  <span className="text-success">✓ Forte</span>
                ) : p.rawValue === "Media" ? (
                  <span className="text-warning">◐ Media</span>
                ) : p.rawValue === "Bassa" ? (
                  <span className="text-danger">✗ Bassa</span>
                ) : p.ok ? (
                  <span className="text-success">✓ OK</span>
                ) : (
                  <span className="text-danger">✗ Fail</span>
                )}
              </div>
            </div>
          );
          })}
        </div>

        {/* Grafico picchi/cali latenza (istogrammi) + potenza segnale */}
        {history.length > 0 && (() => {
          const latencyParams = params.filter((p) => p.unit === "ms");
          const hasPotenza = history.some((h) => h.Potenza_segnale != null);
          const colors = ["#6366f1", "#10b981", "#f59e0b", "#ef4444"];
          const potenzaLabel = (v: number) =>
            v >= 80 ? "Forte" : v >= 40 ? "Media" : "Bassa";
          return (
            <div className="mt-4">
              <h6 className="text-muted mb-2">
                <i className="bi bi-bar-chart me-1" />
                Andamento latenza e potenza segnale (istogrammi)
              </h6>
              <div style={{ height: 220, minHeight: 200 }}>
                <ResponsiveContainer width="100%" height={220} minHeight={200}>
                  <BarChart data={history} margin={{ top: 8, right: 35, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 10 }}
                      unit=" ms"
                      domain={[0, (dataMax) => Math.max(550, (dataMax || 0) * 1.15)]}
                    />
                    {hasPotenza && (
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 10 }}
                        domain={[0, 100]}
                        tickFormatter={(v) => potenzaLabel(v)}
                      />
                    )}
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                      formatter={(val: number, name: string) => [
                        String(name).includes("Potenza") ? potenzaLabel(Number(val)) : `${val} ms`,
                        String(name).replace(/_/g, " "),
                      ]}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: "0.75rem" }}
                      formatter={(val) => val.replace(/_/g, " ")}
                    />
                    <ReferenceLine
                      yAxisId="left"
                      y={500}
                      stroke="#f59e0b"
                      strokeDasharray="4 4"
                      strokeOpacity={0.7}
                    />
                    {latencyParams.map((p, i) => {
                      const key = toDataKey(p.label);
                      const hasData = history.some((h) => h[key] != null);
                      if (!hasData) return null;
                      return (
                        <Bar
                          key={key}
                          yAxisId="left"
                          dataKey={key}
                          name={p.label}
                          fill={colors[i % colors.length]}
                          radius={[4, 4, 0, 0]}
                          maxBarSize={40}
                        />
                      );
                    })}
                    {hasPotenza && (
                      <Bar
                        yAxisId="right"
                        dataKey="Potenza_segnale"
                        name="Potenza segnale"
                        fill="#8b5cf6"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={24}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="small text-muted mt-1">
                Latenza (asse sx): linea tratteggiata = 500 ms. Potenza segnale (asse dx): Forte / Media / Bassa.
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

